import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 *
 * @description Converts an ISO string to a local-time timestamp with format `yyyy-mm-dd hh:mm:ss.SSS`.
 */
export function convertIsoToPlotlyFormat(isoString: string) {
  return dayjs.utc(isoString).local().format('YYYY-MM-DD HH:mm:ss.SSS');
}

/**
 * Converts a local-time timestamp to an ISO string, trying multiple formats for parsing.
 *
 * @param localTimeString - the local time string to be converted
 * @param formats - the array of formats to try for parsing the local time string
 * @returns the ISO string if parsing is successful, or an empty string if all parsing attempts fail
 */
function parseAndConvert(
  localTimeString: string,
  formats: readonly string[]
): string {
  const parsed = dayjs(localTimeString, formats as string[], true);

  if (!parsed.isValid()) {
    return '';
  }

  return parsed.utc().toISOString();
}

/**
 * Takes a local time string in one of several formats and converts it to an ISO string in UTC.
 *
 * Handles both strings with millisecond precision and simple dates
 * that only contain year, month, and day.
 *
 * @param localTimeString - the local time string to be converted
 * @returns the ISO string in UTC ('YYYY-MM-DDTHH:mm:ss.SSSZ')
 * @throws Error if the input string does not match any expected format
 */
export function convertPlotlyFormatToIso(localTimeString: string): string {
  const allowedFormats = [
    'YYYY-MM-DD HH:mm:ss.SSS',
    'YYYY-MM-DD HH:mm:ss',
    'YYYY-MM-DD',
  ] as const;

  const result = parseAndConvert(localTimeString, allowedFormats);

  if (!result) {
    throw new Error(
      `unable to parse the provided date string: ${localTimeString}`
    );
  }

  return result;
}

/**
 *
 * @description Converts a Date Object to a local-time timestamp with format `yyyy-mm-dd hh:mm:ss`.
 */
export function formatDateToLocalString(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

/**
 *
 * @description
 * - Converts a local-time timestamp with format `yyyy-mm-dd hh:mm:ss` to a Date Object
 * - Also handles the `yyyy-mm-ddThh:mm:ss` format
 */
export function parseLocalDateString(dateString: string) {
  const [datePart, timePart] = dateString.includes('T')
    ? dateString.split('T')
    : dateString.split(' ');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes, seconds] = timePart.split(':').map(Number);

  return new Date(year, month - 1, day, hours, minutes, seconds);
}

/**
 *
 * @description Creates a copy of the original date with the milliseconds part removed.
 */
export function removeMilliseconds(date: Date) {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds()
  );
}

/**
 *
 * @description Remove local offset from string date
 */
export function removeLocalOffset(dateString: string) {
  const date = new Date(dateString);
  date.setTime(date.getTime() - date.getTimezoneOffset() * 60 * 1000);
  return date;
}

/**
 *
 * @description Adds local offset and format to local string
 */
export function addLocalOffsetAndFormatToLocalString(
  datestring: string,
  factor = 1
) {
  const newdate = new Date(datestring);
  newdate.setTime(
    newdate.getTime() - newdate.getTimezoneOffset() * 60 * 1000 * factor
  ); // Remove the offset twice
  return newdate.toISOString().replace('T', ' ').replace('Z', '');
}

export const formatDateOnly = (date: Date) => date.toISOString().split('T')[0];

/**
 *
 * @description Set a date at 00:00:00 of the same day
 */
export function changeHourToStartOfDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
}

/**
 *
 * @description Set a date at 23:59:59 of the same day
 */
export function changeHourToEndOfDay(date: Date) {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    23,
    59,
    59
  );
}
